/* eslint-disable max-lines */
// Реестр данных и экшенов всех отчётов модуля reports (как url.js для роутов):
// файл растёт с каждым новым отчётом, лимит 1000 строк снят осознанно
// (прецедент — routes.js, PriceListEditTableV3.tsx).
import { action, observable, runInAction } from 'mobx';
import moment from 'moment';
import compact from 'lodash/compact';
import orderBy from 'lodash/orderBy';
import reportController from './controller';
import {
  PartnerInvoice,
  SettlementWithPartnerReportRequest,
  MobileStatisticsReportRequest,
  MobileStatisticsReportResponse,
  OperatorStatisticsRequest,
  OperatorStatisticsResponse,
  MobileStatisticsExtendedReportRequest,
  MobileStatisticsExtendedReportResponse,
  OrdersWithInvoicesReportRequest,
  OrderBonusesReportRequest,
  OrderBonusesReportResponse,
  UtmAppointmentReportResponse,
  UtmAppointmentReportRequest,
  SovkombankAppointmentReportRequest,
  SovkombankAppointmentReportResponseItem,
  ClientStatisticItem,
  ClientStatisticReportRequest,
  TireServiceItem,
  TireServiceReportRequest,
  TireStorageItem,
  TireStorageReportRequest,
  WorkingHoursItem,
  WorkingHoursReportRequest,
  UtmReportReportRequest,
  UtmReportReportResponse,
  EvacuatorRefusalReportResponseItem,
  EvacuatorRefusalRequest,
  TAvarcomReportResponseItem,
  TAvarcomReportRequest,
  TAvarcomEvacuationReportResponseItem,
  TBankCheckDateRangeReportResponseItem,
  TBankCheckDateReportResponseItem,
  TBankCheckDateReportRequest,
  PromoCheckDateRangeReportRequest,
  PromoCheckDateRangeReportResponseItem,
  PromoCheckDateReportRequest,
  PromoCheckDateReportResponseItem,
  BonusCheckDateRangeReportRequest,
  BonusCheckDateReportRequest,
  BonusCheckDateRangeReportResponseItem,
  BonusCheckDateReportResponseItem,
  TyreStorageReportRequest,
  TyreStorageReportResponseItem,
  CheckTyreServiceReportRequest,
  CheckTyreServiceReportResponseItem,
  CheckTyreServiceDateReportRequest,
  CheckTyreServiceDateReportResponseItem,
  CheckTyreServiceOfficeReportRequest,
  CheckTyreServiceOfficeReportResponseItem,
  PaidInvoicesReportRequestInterface,
  PaidInvoicesReportResponseItemInterface,
  ClientReportForMarketingReportRequestInterface,
  ClientReportForMarketingReportResponseItemInterface,
  VezemkolesaConsentClientItemInterface,
  VezemkolesaConsentRowInterface,
  VezemkolesaConsentsRequestInterface,
  WheelsTransfersRequestInterface,
  WheelsTransfersReportResponseItemInterface,
  AppointmentsTimeIntervalsRequestInterface,
  AppointmentsTimeIntervalsResponseItemInterface,
  WheelsDeliveryItemInterface,
  WheelsDeliveryRequestInterface,
  TireServiceItemInterface,
  TireServiceRequestInterface,
  LossAdjustmentReportItemInterface,
  LossAdjustmentReportRequestInterface,
  LossAdjustmentReportUserItemInterface,
  LossAdjustmentUserReportRequestInterface,
} from './reportApi';

export interface ReportStore {
  settlementWithPartnerReportList: PartnerInvoice[];
  loading: boolean;
  error: string;
  mobileStatisticsReportRequest: MobileStatisticsReportRequest;
  mobileStatisticsReportResponse: MobileStatisticsReportResponse;
  mobileStatisticsExtendedReportRequest: MobileStatisticsExtendedReportRequest;
  mobileStatisticsExtendedReportResponse: MobileStatisticsExtendedReportResponse[];
  operatorStatisticsResponse: OperatorStatisticsResponse[];
  operatorStatisticsRequest: OperatorStatisticsRequest;
  orderBonusesResponse: OrderBonusesReportResponse[];
  orderBonusesRequest: OrderBonusesReportRequest;
  utmAppointmentReportResponse: UtmAppointmentReportResponse[];
  utmAppointmentReportRequest: UtmAppointmentReportRequest;
  sovkombankAppointmentReport: SovkombankAppointmentReportResponseItem[];
  sovkombankAppointmentReportRequest: SovkombankAppointmentReportRequest;
  // Рабочие часы офисов (подсветка нерабочих дней тепловых карт): кэш по
  // диапазону оси ('since|till') — карт с подсветкой несколько и оси у них
  // разные, результаты не перетирают друг друга; loading — ключ диапазона в
  // полёте, ошибки — по диапазонам. Своя пара loading/error: общий loading и
  // error страницы не трогаем — это фоновая догрузка к показанному отчёту.
  sovkombankWorkingHours: Record<string, WorkingHoursItem[]>;
  sovkombankWorkingHoursLoading: string | null;
  sovkombankWorkingHoursError: Record<string, string>;
  // Аналитика заявок ШМ (svc-support, /v1/report/tire/service): заказы
  // шиномонтажа с разбивкой по позициям счёта
  tireServiceOrdersReport: TireServiceItem[];
  tireServiceOrdersReportRequest: TireServiceReportRequest | null;
  // Аналитика хранения (svc-support, /v1/report/tire/storage): заказы хранения
  // шин (сервис 69). Не путать с tyreStorageReport — старый отчёт по хранению
  // колёс на основном API
  tireStorageOrdersReport: TireStorageItem[];
  tireStorageOrdersReportRequest: TireStorageReportRequest | null;
  // Аналитика клиентов (svc-support, /v1/report/client/statistic): клиенты,
  // зарегистрированные за период, с агрегатами по их заказам. Не путать с
  // clientReportForMarketing — старый отчёт по клиентам на основном API
  clientStatisticReport: ClientStatisticItem[];
  clientStatisticReportRequest: ClientStatisticReportRequest | null;
  utmReportReportResponse: UtmReportReportResponse[];
  utmReportReportRequest: UtmReportReportRequest;
  evacuatorRefusalReportResponse: EvacuatorRefusalReportResponseItem[];
  evacuatorRefusalRequest: EvacuatorRefusalRequest;
  tAvarcomReportResponse: TAvarcomReportResponseItem[];
  tAvarcomReportRequest: TAvarcomReportRequest;
  tAvarcomEvacuationReport: TAvarcomEvacuationReportResponseItem[];
  tAvarcomEvacuationReportRequest: TAvarcomReportRequest;
  tBankCheckReport: TBankCheckDateRangeReportResponseItem[];
  tBankCheckReportRequest: TAvarcomReportRequest;
  tBankCheckDateReport: TBankCheckDateReportResponseItem[];
  tBankCheckDateReportRequest: TBankCheckDateReportRequest;
  promoCheckDateRangeReportRequest: PromoCheckDateRangeReportRequest;
  promoCheckDateRangeReport: PromoCheckDateRangeReportResponseItem[];
  promoCheckDateReportRequest: PromoCheckDateReportRequest;
  promoCheckDateReport: PromoCheckDateReportResponseItem[];
  bonusCheckDateRangeReport: BonusCheckDateRangeReportResponseItem[];
  bonusCheckDateRangeReportRequest: BonusCheckDateRangeReportRequest;
  bonusCheckDateReport: BonusCheckDateReportResponseItem[];
  bonusCheckDateReportRequest: BonusCheckDateReportRequest;
  tyreStorageReportRequest: TyreStorageReportRequest;
  tyreStorageReport: TyreStorageReportResponseItem[];
  checkTyreServiceReportRequest: CheckTyreServiceReportRequest;
  checkTyreServiceReport: CheckTyreServiceReportResponseItem[];
  checkTyreServiceDateReportRequest: CheckTyreServiceDateReportRequest;
  checkTyreServiceDateReport: CheckTyreServiceDateReportResponseItem[];
  checkTyreServiceOfficeReportRequest: CheckTyreServiceOfficeReportRequest;
  checkTyreServiceOfficeReport: CheckTyreServiceOfficeReportResponseItem[];
  checkTyreServiceDateSelectedOffice: string;
  paidInvoicesRequest: PaidInvoicesReportRequestInterface;
  paidInvoices: PaidInvoicesReportResponseItemInterface[];
  clientReportForMarketingRequest: ClientReportForMarketingReportRequestInterface;
  clientReportForMarketing: ClientReportForMarketingReportResponseItemInterface[];
  vezemkolesaConsents: VezemkolesaConsentRowInterface[];
  wheelsTransfersRequest: WheelsTransfersRequestInterface;
  wheelsTransfersReport: WheelsTransfersReportResponseItemInterface[];
  appointmentsTimeIntervalsRequest: AppointmentsTimeIntervalsRequestInterface;
  appointmentsTimeIntervalsReport: AppointmentsTimeIntervalsResponseItemInterface[];
  wheelsDeliveryReport: WheelsDeliveryItemInterface[];
  wheelsDeliveryRequest: WheelsDeliveryRequestInterface;
  tireServiceReport: TireServiceItemInterface[];
  tireServiceRequest: TireServiceRequestInterface;
  lossAdjustmentReport: LossAdjustmentReportItemInterface[];
  lossAdjustmentReportRequest: LossAdjustmentReportRequestInterface;
  lossAdjustmentUserReport: LossAdjustmentReportUserItemInterface[];
  lossAdjustmentUserReportRequest: LossAdjustmentUserReportRequestInterface;
}

const initialValues: ReportStore = {
  settlementWithPartnerReportList: [],
  loading: false,
  error: null,
  mobileStatisticsReportRequest: null,
  mobileStatisticsReportResponse: null,
  mobileStatisticsExtendedReportRequest: null,
  mobileStatisticsExtendedReportResponse: [],
  operatorStatisticsResponse: [],
  operatorStatisticsRequest: null,
  orderBonusesResponse: [],
  orderBonusesRequest: null,
  utmAppointmentReportResponse: [],
  utmAppointmentReportRequest: null,
  sovkombankAppointmentReport: [],
  sovkombankAppointmentReportRequest: null,
  sovkombankWorkingHours: {},
  sovkombankWorkingHoursLoading: null,
  sovkombankWorkingHoursError: {},
  tireServiceOrdersReport: [],
  tireServiceOrdersReportRequest: null,
  tireStorageOrdersReport: [],
  tireStorageOrdersReportRequest: null,
  clientStatisticReport: [],
  clientStatisticReportRequest: null,
  utmReportReportResponse: [],
  utmReportReportRequest: null,
  evacuatorRefusalReportResponse: [],
  evacuatorRefusalRequest: null,
  tAvarcomReportResponse: [],
  tAvarcomReportRequest: null,
  tAvarcomEvacuationReport: [],
  tAvarcomEvacuationReportRequest: null,
  tBankCheckReport: [],
  tBankCheckReportRequest: null,
  tBankCheckDateReport: [],
  tBankCheckDateReportRequest: null,
  promoCheckDateRangeReportRequest: null,
  promoCheckDateRangeReport: [],
  promoCheckDateReportRequest: null,
  promoCheckDateReport: [],
  bonusCheckDateRangeReport: [],
  bonusCheckDateRangeReportRequest: null,
  bonusCheckDateReport: [],
  bonusCheckDateReportRequest: null,
  tyreStorageReportRequest: null,
  tyreStorageReport: [],
  checkTyreServiceReportRequest: null,
  checkTyreServiceReport: [],
  checkTyreServiceDateReportRequest: null,
  checkTyreServiceDateReport: [],
  checkTyreServiceOfficeReportRequest: null,
  checkTyreServiceOfficeReport: [],
  checkTyreServiceDateSelectedOffice: null,
  paidInvoicesRequest: null,
  paidInvoices: [],
  clientReportForMarketingRequest: null,
  clientReportForMarketing: [],
  vezemkolesaConsents: [],
  wheelsTransfersRequest: null,
  wheelsTransfersReport: [],
  appointmentsTimeIntervalsRequest: null,
  appointmentsTimeIntervalsReport: [],
  wheelsDeliveryReport: [],
  wheelsDeliveryRequest: null,
  tireServiceReport: [],
  tireServiceRequest: null,
  lossAdjustmentReport: [],
  lossAdjustmentReportRequest: null,
  lossAdjustmentUserReport: [],
  lossAdjustmentUserReportRequest: null,
};

export const reportStore = observable(initialValues);

// ==================== HELPER FUNCTIONS ====================

const handleError = (error: string) => {
  reportStore.error = error;
};

const setLoading = action(() => {
  reportStore.loading = true;
});

const clearError = action(() => {
  reportStore.error = null;
});

// Универсальная функция загрузки отчёта
interface FetchReportOptions {
  method: string;
  request: any;
  path?: string; // 'data', 'data.items', 'data.content'
  transform?: (data: any) => any;
  sort?: { field: string; order: 'asc' | 'desc' };
  prepareRequest?: boolean; // обработать serviceIds
  setState: (data: any, request?: any) => void;
  onError?: () => void;
}

const fetchReport = async (options: FetchReportOptions) => {
  try {
    clearError();
    setLoading();

    let finalRequest = options.request;
    if (options.prepareRequest) {
      finalRequest = {
        ...options.request,
        serviceIds: compact(options.request.serviceIds ?? []),
      };
    }

    const response = await (reportController as any)[options.method](finalRequest);

    runInAction(() => {
      // 401 и сетевые сбои приходят без errorData (интерсептор api.js отдаёт
      // их как «успех»), но с error=true / connectError и телом ошибки вместо
      // данных. Без этой проверки fetchReport шёл бы в «успешный» путь,
      // извлекал data.items === undefined и отчёт молча показывал пустую
      // таблицу (репрод: отчёт «Аналитика хранения», дев, 2026-09-18).
      if (response.errorData || response.error || response.connectError) {
        reportStore.error = response.errorData?.message
          ?? (typeof response.data?.message === 'string' ? response.data.message : null)
          ?? 'Ошибка загрузки данных';
        options.onError?.();
        reportStore.loading = false;
        return;
      }

      let data = response;
      if (options.path) {
        for (const part of options.path.split('.')) {
          data = data?.[part];
        }
      }

      if (options.transform) {
        data = options.transform(data);
      }

      if (options.sort) {
        data = orderBy(data, [options.sort.field], [options.sort.order]);
      }

      options.setState(data, options.prepareRequest ? finalRequest : options.request);
      reportStore.loading = false;
    });
  } catch (err: any) {
    handleError(err.message);
    reportStore.loading = false;
  }
};

// Трансформация для инвойсов
const transformInvoiceData = (items: any[]) => {
  return items.map((v) => ({
    ...v,
    amount: v.invoiceAmount - v.invoiceDiscount,
    paymentDate: v.paymentDate ? moment.unix(v.paymentDate).local().format('DD.MM.YYYY HH:mm') : null,
    orderCreatedDate: moment.unix(v.orderCreatedDate).local().format('DD.MM.YYYY HH:mm'),
    orderImplementedDate: v.orderImplementedDate
      ? moment.unix(v.orderImplementedDate).local().format('DD.MM.YYYY HH:mm')
      : null,
  }));
};

// Трансформация для шиномонтажа
const transformTyreServiceData = (items: any[]) => {
  return items.map((v) => ({
    ...v,
    notPaidOrdersCount:
      Math.round(v.totalOrders ?? 0) -
      Math.round(v.paidOrdersCount ?? 0) -
      Math.round(v.promoOrdersCount ?? 0),
    averageOrderCost:
      Math.round(v.paidOrdersCount ?? 0) > 0
        ? Math.round((v.paidOrdersSum ?? 0) / Math.round(v.paidOrdersCount ?? 0) * 100) / 100
        : undefined,
  }));
};

// ==================== ACTIONS ====================

export const reportsActions = {
  handleError,
  setLoading,
  clearError,

  // Mobile Statistics
  async getMobileStatisticsReport(request: MobileStatisticsReportRequest) {
    await fetchReport({
      method: 'getMobileStatisticsReport',
      request,
      path: 'data',
      setState: (data: MobileStatisticsReportResponse, req) => {
        reportStore.mobileStatisticsReportResponse = data;
        reportStore.mobileStatisticsReportRequest = req;
      },
      onError: () => {
        reportStore.mobileStatisticsReportResponse = null;
        reportStore.mobileStatisticsReportRequest = null;
      },
    });
  },

  clearMobileStatisticsReport() {
    runInAction(() => {
      reportStore.mobileStatisticsReportRequest = null;
      reportStore.mobileStatisticsReportResponse = null;
    });
  },

  // Mobile Statistics Extended
  async getMobileStatisticsExtendedReport(request: MobileStatisticsExtendedReportRequest) {
    await fetchReport({
      method: 'getMobileStatisticsExtendedReport',
      request,
      path: 'data',
      setState: (data: MobileStatisticsExtendedReportResponse[], req) => {
        reportStore.mobileStatisticsExtendedReportResponse = data;
        reportStore.mobileStatisticsExtendedReportRequest = req;
      },
      onError: () => {
        reportStore.mobileStatisticsExtendedReportResponse = [];
        reportStore.mobileStatisticsExtendedReportRequest = null;
      },
    });
  },

  clearMobileStatisticsExtendedReport() {
    runInAction(() => {
      reportStore.mobileStatisticsExtendedReportRequest = null;
      reportStore.mobileStatisticsExtendedReportResponse = [];
    });
  },

  // Settlement With Partner
  async getSettlementWithPartnerReport(data: SettlementWithPartnerReportRequest) {
    await fetchReport({
      method: 'getSettlementWithPartnerReport',
      request: data,
      path: 'data.items',
      transform: transformInvoiceData,
      prepareRequest: true,
      setState: (data: PartnerInvoice[]) => {
        reportStore.settlementWithPartnerReportList = data;
      },
    });
  },

  // Orders With Invoices
  async getOrdersWithInvoicesReport(data: OrdersWithInvoicesReportRequest) {
    await fetchReport({
      method: 'getOrdersWithInvoicesReport',
      request: data,
      path: 'data.items',
      transform: transformInvoiceData,
      prepareRequest: true,
      setState: (data: PartnerInvoice[]) => {
        reportStore.settlementWithPartnerReportList = data;
      },
    });
  },

  // Toggle methods
  setSettlementWithPartnerReportToggle(invoiceId: string, toggle: boolean) {
    runInAction(() => {
      reportStore.settlementWithPartnerReportList =
        reportStore.settlementWithPartnerReportList.map((v) => ({
          ...v,
          toggle: v.invoiceId === invoiceId ? toggle : v.toggle ?? false,
        }));
    });
  },

  setSettlementWithPartnerReportToggleAll() {
    runInAction(() => {
      reportStore.settlementWithPartnerReportList =
        reportStore.settlementWithPartnerReportList.map((v) => ({
          ...v,
          toggle: !v.reconciliationActId ? true : v.toggle,
        }));
    });
  },

  // Operator Statistics (special case with groupByUser)
  async getOperatorStatistics(request: OperatorStatisticsRequest, groupByUser: boolean) {
    try {
      clearError();
      setLoading();

      const response = await (groupByUser
        ? reportController.getOperatorGroupByUserStatistics(request)
        : reportController.getOperatorStatistics(request));

      runInAction(() => {
        if (response.errorData) {
          reportStore.error = response.errorData.message;
          reportStore.loading = false;
          return;
        }
        reportStore.operatorStatisticsResponse = response.data.items;
        reportStore.operatorStatisticsRequest = request;
        reportStore.loading = false;
      });
    } catch (err: any) {
      handleError(err.message);
      reportStore.loading = false;
    }
  },

  // Order Bonuses
  async getOrdersBonuses(request: OrderBonusesReportRequest) {
    await fetchReport({
      method: 'getOrdersBonuses',
      request,
      path: 'data.items',
      setState: (data: OrderBonusesReportResponse[], req) => {
        reportStore.orderBonusesResponse = data;
        reportStore.orderBonusesRequest = req;
      },
      onError: () => {
        reportStore.orderBonusesResponse = [];
        reportStore.orderBonusesRequest = null;
      },
    });
  },

  clearOrdersBonusesReport() {
    runInAction(() => {
      reportStore.orderBonusesRequest = null;
      reportStore.orderBonusesResponse = [];
    });
  },

  // UTМ Appointment
  async getUtmAppointmentReport(data: UtmAppointmentReportRequest) {
    await fetchReport({
      method: 'getUtmAppointmentReport',
      request: data,
      path: 'data.items',
      transform: (items: any[]) =>
        items.map((v) => ({
          ...v,
          startDateTime: v.paymentDate
            ? moment.unix(v.startDateTime).local().format('DD.MM.YYYY HH:mm')
            : null,
        })),
      setState: (data: UtmAppointmentReportResponse[], req) => {
        reportStore.utmAppointmentReportResponse = data;
        reportStore.utmAppointmentReportRequest = req;
      },
      onError: () => {
        reportStore.utmAppointmentReportResponse = [];
        reportStore.utmAppointmentReportRequest = null;
      },
    });
  },

  // Sovkombank Appointment
  async getSovkombankAppointmentReport(data: SovkombankAppointmentReportRequest) {
    await fetchReport({
      method: 'getSovkombankAppointmentReport',
      request: data,
      path: 'data.items',
      setState: (data: SovkombankAppointmentReportResponseItem[], req) => {
        reportStore.sovkombankAppointmentReport = data;
        reportStore.sovkombankAppointmentReportRequest = req;
      },
      onError: () => {
        reportStore.sovkombankAppointmentReport = [];
        reportStore.sovkombankAppointmentReportRequest = null;
      },
    });
  },

  clearSovkombankAppointmentReport() {
    runInAction(() => {
      reportStore.sovkombankAppointmentReport = [];
      reportStore.sovkombankAppointmentReportRequest = null;
    });
  },

  // Tire Service Orders — аналитика заявок ШМ (svc-support)
  async getTireServiceOrdersReport(data: TireServiceReportRequest) {
    await fetchReport({
      method: 'getTireServiceOrdersReport',
      request: data,
      path: 'data.items',
      setState: (data: TireServiceItem[], req) => {
        reportStore.tireServiceOrdersReport = data;
        reportStore.tireServiceOrdersReportRequest = req;
      },
      onError: () => {
        reportStore.tireServiceOrdersReport = [];
        reportStore.tireServiceOrdersReportRequest = null;
      },
    });
  },

  clearTireServiceOrdersReport() {
    runInAction(() => {
      reportStore.tireServiceOrdersReport = [];
      reportStore.tireServiceOrdersReportRequest = null;
    });
  },

  // Tire Storage Orders — аналитика хранения (svc-support)
  async getTireStorageOrdersReport(data: TireStorageReportRequest) {
    await fetchReport({
      method: 'getTireStorageOrdersReport',
      request: data,
      path: 'data.items',
      setState: (data: TireStorageItem[], req) => {
        reportStore.tireStorageOrdersReport = data;
        reportStore.tireStorageOrdersReportRequest = req;
      },
      onError: () => {
        reportStore.tireStorageOrdersReport = [];
        reportStore.tireStorageOrdersReportRequest = null;
      },
    });
  },

  clearTireStorageOrdersReport() {
    runInAction(() => {
      reportStore.tireStorageOrdersReport = [];
      reportStore.tireStorageOrdersReportRequest = null;
    });
  },

  // Client Statistic — аналитика клиентов (svc-support)
  async getClientStatisticReport(data: ClientStatisticReportRequest) {
    await fetchReport({
      method: 'getClientStatisticReport',
      request: data,
      path: 'data.items',
      setState: (data: ClientStatisticItem[], req) => {
        reportStore.clientStatisticReport = data;
        reportStore.clientStatisticReportRequest = req;
      },
      onError: () => {
        reportStore.clientStatisticReport = [];
        reportStore.clientStatisticReportRequest = null;
      },
    });
  },

  clearClientStatisticReport() {
    runInAction(() => {
      reportStore.clientStatisticReport = [];
      reportStore.clientStatisticReportRequest = null;
    });
  },

  // Рабочие часы офисов — подсветка нерабочих дней тепловых карт «офис ×
  // период». Намеренно не fetchReport: он поднимает общий loading страницы и
  // пишет в общий error, а это фоновая догрузка к уже показанному отчёту —
  // её состояние видит только подсказка у тумблера «Показать выходные».
  // Результат кладётся в кэш по ключу диапазона: вторая карта с другой осью
  // не сбрасывает подсветку первой.
  async getSovkombankWorkingHours(request: WorkingHoursReportRequest) {
    const rangeKey = `${ request.sinceBeginDate }|${ request.tillBeginDate }`;
    runInAction(() => {
      reportStore.sovkombankWorkingHoursLoading = rangeKey;
      delete reportStore.sovkombankWorkingHoursError[rangeKey];
    });
    try {
      const response: any = await reportController.getSovkombankWorkingHours(request);
      runInAction(() => {
        reportStore.sovkombankWorkingHoursLoading = null;
        if (response.errorData) {
          reportStore.sovkombankWorkingHoursError[rangeKey] = response.errorData.message;
          return;
        }
        reportStore.sovkombankWorkingHours[rangeKey] = response?.data?.items ?? [];
      });
    } catch (err: any) {
      runInAction(() => {
        reportStore.sovkombankWorkingHoursLoading = null;
        reportStore.sovkombankWorkingHoursError[rangeKey] = err?.message ?? 'Ошибка загрузки рабочих часов';
      });
    }
  },

  // UTМ Order
  async getUtmOrderReport(data: UtmReportReportRequest) {
    await fetchReport({
      method: 'getUtmOrderReport',
      request: data,
      path: 'data.items',
      prepareRequest: true,
      setState: (data: UtmReportReportResponse[], req) => {
        reportStore.utmReportReportResponse = data;
        reportStore.utmReportReportRequest = req;
      },
      onError: () => {
        reportStore.utmReportReportResponse = [];
        reportStore.utmReportReportRequest = null;
      },
    });
  },

  // Evacuator Refusal
  async getEvacuatorRefusalReport(data: EvacuatorRefusalRequest) {
    await fetchReport({
      method: 'getEvacuatorRefusalReport',
      request: data,
      path: 'data.items',
      setState: (data: EvacuatorRefusalReportResponseItem[], req) => {
        reportStore.evacuatorRefusalReportResponse = data;
        reportStore.evacuatorRefusalRequest = req;
      },
      onError: () => {
        reportStore.evacuatorRefusalReportResponse = [];
        reportStore.evacuatorRefusalRequest = null;
      },
    });
  },

  // T-Avarcom
  async getTAvarcomReport(data: TAvarcomReportRequest) {
    await fetchReport({
      method: 'getTAvarcomReport',
      request: data,
      path: 'data.items',
      setState: (data: TAvarcomReportResponseItem[], req) => {
        reportStore.tAvarcomReportResponse = data;
        reportStore.tAvarcomReportRequest = req;
      },
      onError: () => {
        reportStore.tAvarcomReportResponse = [];
        reportStore.tAvarcomReportRequest = null;
      },
    });
  },

  // T-Avarcom Evacuation
  async getTAvarcomEvacuationReport(data: TAvarcomReportRequest) {
    await fetchReport({
      method: 'getTAvarcomEvacuationReport',
      request: data,
      path: 'data.items',
      setState: (data: TAvarcomEvacuationReportResponseItem[], req) => {
        reportStore.tAvarcomEvacuationReport = data;
        reportStore.tAvarcomEvacuationReportRequest = req;
      },
      onError: () => {
        reportStore.tAvarcomEvacuationReport = [];
        reportStore.tAvarcomEvacuationReportRequest = null;
      },
    });
  },

  // T-Bank Check
  async getTBankCheckReport(data: TAvarcomReportRequest) {
    await fetchReport({
      method: 'getTBankCheckReport',
      request: data,
      path: 'data.items',
      sort: { field: 'date', order: 'desc' },
      setState: (data: TBankCheckDateRangeReportResponseItem[], req) => {
        reportStore.tBankCheckReport = data;
        reportStore.tBankCheckReportRequest = req;
      },
      onError: () => {
        reportStore.tBankCheckReport = [];
        reportStore.tBankCheckReportRequest = null;
      },
    });
  },

  // T-Bank Check Date
  async getTBankCheckDateReport(data: TBankCheckDateReportRequest) {
    await fetchReport({
      method: 'getTBankCheckDateReport',
      request: data,
      path: 'data.items',
      setState: (data: TBankCheckDateReportResponseItem[], req) => {
        reportStore.tBankCheckDateReport = data;
        reportStore.tBankCheckDateReportRequest = req;
      },
      onError: () => {
        reportStore.tBankCheckDateReport = [];
        reportStore.tBankCheckDateReportRequest = null;
      },
    });
  },

  clearTBankCheckDateReport() {
    runInAction(() => {
      reportStore.tBankCheckDateReport = [];
      reportStore.tBankCheckDateReportRequest = null;
    });
  },

  // Promo Check Date Range
  async getPromoCheckDateRangeReport(data: PromoCheckDateRangeReportRequest) {
    await fetchReport({
      method: 'getPromoCheckDateRangeReport',
      request: data,
      path: 'data.items',
      sort: { field: 'usageDate', order: 'desc' },
      setState: (data: PromoCheckDateRangeReportResponseItem[], req) => {
        reportStore.promoCheckDateRangeReport = data;
        reportStore.promoCheckDateRangeReportRequest = req;
      },
      onError: () => {
        reportStore.promoCheckDateRangeReport = [];
        reportStore.promoCheckDateRangeReportRequest = null;
      },
    });
  },

  clearPromoCheckDateRangeReport() {
    runInAction(() => {
      reportStore.promoCheckDateRangeReport = [];
      reportStore.promoCheckDateRangeReportRequest = null;
    });
  },

  // Promo Check Date
  async getPromoCheckDateReport(data: PromoCheckDateReportRequest) {
    await fetchReport({
      method: 'getPromoCheckDateReport',
      request: data,
      path: 'data.items',
      setState: (data: PromoCheckDateReportResponseItem[], req) => {
        reportStore.promoCheckDateReport = data;
        reportStore.promoCheckDateReportRequest = req;
      },
      onError: () => {
        reportStore.promoCheckDateReport = [];
        reportStore.promoCheckDateReportRequest = null;
      },
    });
  },

  clearPromoCheckDateReport() {
    runInAction(() => {
      reportStore.promoCheckDateReport = [];
      reportStore.promoCheckDateReportRequest = null;
    });
  },

  // Bonus Check Date Range
  async getBonusCheckDateRangeReport(data: BonusCheckDateRangeReportRequest) {
    await fetchReport({
      method: 'getBonusCheckDateRangeReport',
      request: data,
      path: 'data.items',
      sort: { field: 'operationDate', order: 'desc' },
      setState: (data: BonusCheckDateRangeReportResponseItem[], req) => {
        reportStore.bonusCheckDateRangeReport = data;
        reportStore.bonusCheckDateRangeReportRequest = req;
      },
      onError: () => {
        reportStore.bonusCheckDateRangeReport = [];
        reportStore.bonusCheckDateRangeReportRequest = null;
      },
    });
  },

  clearBonusCheckDateRangeReport() {
    runInAction(() => {
      reportStore.bonusCheckDateRangeReport = [];
      reportStore.bonusCheckDateRangeReportRequest = null;
    });
  },

  // Bonus Check Date
  async getBonusCheckDateReport(data: BonusCheckDateReportRequest) {
    await fetchReport({
      method: 'getBonusCheckDateReport',
      request: data,
      path: 'data.items',
      setState: (data: BonusCheckDateReportResponseItem[], req) => {
        reportStore.bonusCheckDateReport = data;
        reportStore.bonusCheckDateReportRequest = req;
      },
      onError: () => {
        reportStore.bonusCheckDateReport = [];
        reportStore.bonusCheckDateReportRequest = null;
      },
    });
  },

  clearBonusCheckDateReport() {
    runInAction(() => {
      reportStore.bonusCheckDateReport = [];
      reportStore.bonusCheckDateReportRequest = null;
    });
  },

  // Tyre Storage
  async getTyreStorageReport(data: TyreStorageReportRequest) {
    await fetchReport({
      method: 'getTyreStorageReport',
      request: data,
      path: 'data.items',
      sort: { field: 'createdDate', order: 'asc' },
      setState: (data: TyreStorageReportResponseItem[], req) => {
        reportStore.tyreStorageReport = data;
        reportStore.tyreStorageReportRequest = req;
      },
      onError: () => {
        reportStore.tyreStorageReport = [];
        reportStore.tyreStorageReportRequest = null;
      },
    });
  },

  clearTyreStorageReport() {
    runInAction(() => {
      reportStore.tyreStorageReport = [];
      reportStore.tyreStorageReportRequest = null;
    });
  },

  // Check Tyre Service
  async getCheckTyreServiceReport(data: CheckTyreServiceReportRequest) {
    await fetchReport({
      method: 'getCheckTyreServiceReport',
      request: data,
      path: 'data.items',
      transform: transformTyreServiceData,
      sort: { field: 'officeName', order: 'asc' },
      setState: (data: CheckTyreServiceReportResponseItem[], req) => {
        reportStore.checkTyreServiceReport = data;
        reportStore.checkTyreServiceReportRequest = req;
      },
      onError: () => {
        reportStore.checkTyreServiceReport = [];
        reportStore.checkTyreServiceReportRequest = null;
      },
    });
  },

  clearCheckTyreServiceReport() {
    runInAction(() => {
      reportStore.checkTyreServiceReport = [];
      reportStore.checkTyreServiceReportRequest = null;
    });
  },

  // Check Tyre Service Date
  async getCheckTyreServiceDateReport(data: CheckTyreServiceDateReportRequest) {
    await fetchReport({
      method: 'getCheckTyreServiceDateReport',
      request: data,
      path: 'data.items',
      sort: { field: 'orderNumber', order: 'asc' },
      setState: (data: CheckTyreServiceDateReportResponseItem[], req) => {
        reportStore.checkTyreServiceDateReport = data;
        reportStore.checkTyreServiceDateReportRequest = req;
      },
      onError: () => {
        reportStore.checkTyreServiceDateReport = [];
        reportStore.checkTyreServiceDateReportRequest = null;
      },
    });
  },

  clearCheckTyreServiceDateReport() {
    runInAction(() => {
      reportStore.checkTyreServiceDateReport = [];
      reportStore.checkTyreServiceDateReportRequest = null;
    });
  },

  // Check Tyre Service Office
  async getCheckTyreServiceOfficeReport(
    data: CheckTyreServiceOfficeReportRequest,
    officeName: string
  ) {
    await fetchReport({
      method: 'getCheckTyreServiceOfficeReport',
      request: data,
      path: 'data.items',
      transform: transformTyreServiceData,
      sort: { field: 'orderDate', order: 'asc' },
      setState: (data: CheckTyreServiceOfficeReportResponseItem[], req) => {
        reportStore.checkTyreServiceOfficeReport = data;
        reportStore.checkTyreServiceOfficeReportRequest = req;
        reportStore.checkTyreServiceDateSelectedOffice = officeName;
      },
      onError: () => {
        reportStore.checkTyreServiceOfficeReport = [];
        reportStore.checkTyreServiceOfficeReportRequest = null;
        reportStore.checkTyreServiceDateSelectedOffice = null;
      },
    });
  },

  clearCheckTyreServiceOfficeReport() {
    runInAction(() => {
      reportStore.checkTyreServiceOfficeReport = [];
      reportStore.checkTyreServiceOfficeReportRequest = null;
      reportStore.checkTyreServiceDateSelectedOffice = null;
    });
  },

  // Paid Invoices
  async getPaidInvoicesReport(request: PaidInvoicesReportRequestInterface) {
    await fetchReport({
      method: 'getPaidInvoicesReport',
      request,
      path: 'data.items',
      setState: (data: PaidInvoicesReportResponseItemInterface[], req) => {
        reportStore.paidInvoices = data;
        reportStore.paidInvoicesRequest = req;
      },
      onError: () => {
        reportStore.paidInvoices = [];
        reportStore.paidInvoicesRequest = null;
      },
    });
  },

  // Client Report For Marketing
  async getClientReportForMarketingReport(data: ClientReportForMarketingReportRequestInterface) {
    await fetchReport({
      method: 'getClientReportForMarketingReport',
      request: data,
      path: 'data',
      setState: (data: ClientReportForMarketingReportResponseItemInterface[], req) => {
        reportStore.clientReportForMarketing = data;
        reportStore.clientReportForMarketingRequest = req;
      },
      onError: () => {
        reportStore.clientReportForMarketing = [];
        reportStore.clientReportForMarketingRequest = null;
      },
    });
  },

  clearClientReportForMarketingReport() {
    runInAction(() => {
      reportStore.clientReportForMarketing = [];
      reportStore.clientReportForMarketingRequest = null;
    });
  },

  // Персональные данные: события согласий ВезёмКолёса плоской таблицей —
  // строка на каждое согласие (данные клиента дублируются в строках его согласий).
  // Бек отдаёт клиента с вложенным массивом подходящих под фильтр согласий —
  // разворачиваем в строки здесь, таблица отчёта работает с плоским списком.
  async getVezemkolesaConsentsClients(request: VezemkolesaConsentsRequestInterface = {}) {
    await fetchReport({
      method: 'getClientsWithVezemkolesaConsents',
      request,
      path: 'data.items',
      transform: (data: VezemkolesaConsentClientItemInterface[]) => {
        let rowNumber = 0;
        return (data ?? []).flatMap((client) => (client.vezemkolesaPrivacyConsents ?? []).map(
          (consent, consentIndex) => ({
            ...consent,
            rowId: `${ client.clientId ?? 'client' }-${ consentIndex }`,
            rowNumber: ++rowNumber,
            clientId: client.clientId,
            clientLastName: client.lastName,
            clientFirstName: client.firstName,
            clientPhoneNumber: client.phoneNumber,
            clientEmail: client.emailFromVezemkolesa,
          })
        ));
      },
      setState: (data: VezemkolesaConsentRowInterface[]) => {
        reportStore.vezemkolesaConsents = data;
      },
      onError: () => {
        reportStore.vezemkolesaConsents = [];
      },
    });
  },

  // Wheels Transfers
  async getWheelsTransfersReport(request: WheelsTransfersRequestInterface) {
    await fetchReport({
      method: 'getWheelsTransfersReport',
      request,
      path: 'data.items',
      setState: (data: WheelsTransfersReportResponseItemInterface[], req) => {
        reportStore.wheelsTransfersReport = data;
        reportStore.wheelsTransfersRequest = req;
      },
      onError: () => {
        reportStore.wheelsTransfersReport = [];
        reportStore.wheelsTransfersRequest = null;
      },
    });
  },

  clearWheelsTransfersReport() {
    runInAction(() => {
      reportStore.wheelsTransfersReport = [];
      reportStore.wheelsTransfersRequest = null;
    });
  },

  // Appointments Time Intervals
  async getAppointmentsTimeIntervalsReport(request: AppointmentsTimeIntervalsRequestInterface) {
    await fetchReport({
      method: 'getAppointmentsTimeIntervalsReport',
      request,
      path: 'data.items',
      setState: (data: AppointmentsTimeIntervalsResponseItemInterface[], req) => {
        reportStore.appointmentsTimeIntervalsReport = data;
        reportStore.appointmentsTimeIntervalsRequest = req;
      },
      onError: () => {
        reportStore.appointmentsTimeIntervalsReport = [];
        reportStore.appointmentsTimeIntervalsRequest = null;
      },
    });
  },

  clearAppointmentsTimeIntervalsReport() {
    runInAction(() => {
      reportStore.appointmentsTimeIntervalsReport = [];
      reportStore.appointmentsTimeIntervalsRequest = null;
    });
  },

  // Wheels Delivery
  async getWheelsDeliveryReport(request: WheelsDeliveryRequestInterface) {
    await fetchReport({
      method: 'getWheelsDeliveryReport',
      request,
      path: 'data.content',
      setState: (data: WheelsDeliveryItemInterface[], req) => {
        reportStore.wheelsDeliveryReport = data;
        reportStore.wheelsDeliveryRequest = req;
      },
      onError: () => {
        reportStore.wheelsDeliveryReport = [];
        reportStore.wheelsDeliveryRequest = null;
      },
    });
  },

  clearWheelsDeliveryReport() {
    runInAction(() => {
      reportStore.wheelsDeliveryReport = [];
      reportStore.wheelsDeliveryRequest = null;
    });
  },

  // Tire Service
  async getTireServiceReport(request: TireServiceRequestInterface) {
    await fetchReport({
      method: 'getTireServiceReport',
      request,
      path: 'data.items',
      setState: (data: TireServiceItemInterface[], req) => {
        reportStore.tireServiceReport = data;
        reportStore.tireServiceRequest = req;
      },
      onError: () => {
        reportStore.tireServiceReport = [];
        reportStore.tireServiceRequest = null;
      },
    });
  },

  clearTireServiceReport() {
    runInAction(() => {
      reportStore.tireServiceReport = [];
      reportStore.tireServiceRequest = null;
    });
  },

  // Loss Adjustment
  async getLossAdjustmentReport(request: LossAdjustmentReportRequestInterface) {
    await fetchReport({
      method: 'getLossAdjustmentReport',
      request,
      path: 'data.items',
      setState: (data: LossAdjustmentReportItemInterface[], req) => {
        reportStore.lossAdjustmentReport = data;
        reportStore.lossAdjustmentReportRequest = req;
      },
      onError: () => {
        reportStore.lossAdjustmentReport = [];
        reportStore.lossAdjustmentReportRequest = null;
      },
    });
  },

  clearLossAdjustmentReport() {
    runInAction(() => {
      reportStore.lossAdjustmentReport = [];
      reportStore.lossAdjustmentReportRequest = null;
    });
  },

  // Loss Adjustment User
  async getLossAdjustmentUserReport(request: LossAdjustmentUserReportRequestInterface) {
    await fetchReport({
      method: 'getLossAdjustmentUserReport',
      request,
      path: 'data.items',
      setState: (data: LossAdjustmentReportUserItemInterface[], req) => {
        reportStore.lossAdjustmentUserReport = data;
        reportStore.lossAdjustmentUserReportRequest = req;
      },
      onError: () => {
        reportStore.lossAdjustmentUserReport = [];
        reportStore.lossAdjustmentUserReportRequest = null;
      },
    });
  },

  clearLossAdjustmentUserReport() {
    runInAction(() => {
      reportStore.lossAdjustmentUserReport = [];
      reportStore.lossAdjustmentUserReportRequest = null;
    });
  },
};